import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { RfqBadge, RfqMethodBadge } from '@/components/StatusBadge';
import { shortDateTime, untilLabel, won } from '@/lib/format';
import { useRfq, useRfqActions } from '@/lib/queries';
import type { RfqBidView, VendorRfqDetail } from '@/lib/types';

/**
 * 견적·입찰 한 건 — 공고를 읽고 의사를 밝히고 금액을 내는 자리(DOC-98 §5-1).
 *
 * <h2>마감 뒤에도 응찰 칸이 사라지지 않는다</h2>
 * 「마감되었습니다」로 바뀔 뿐이다. 칸째 사라지면 늦게 들어온 사람은 <b>무엇을 놓쳤는지</b> 알 수 없다.
 *
 * <h2>제출 가능 여부는 서버의 판정을 읽는다</h2>
 * `closed`(서버 시계)와 `status` 로만 가른다 — 화면이 `new Date()` 로 마감을 다시 재지 않는다.
 * 그래도 마지막 판정은 서버다: 늦게 눌린 제출은 서버가 거절하고 화면은 한 문장으로 답한다.
 *
 * <h2>남의 응찰은 자리가 없다</h2>
 * 몇 곳이 냈는지조차 내려오지 않는다(`ExternalRfqService`). 그래서 경쟁 현황 칸을 만들지 않는다 —
 * 빈 자리를 세우면 언젠가 누군가 그 자리를 채우려 든다.
 */
export function RfqDetailPage() {
  const { rfqId = '' } = useParams();
  const rfq = useRfq(rfqId);
  const { intent, bid, withdraw } = useRfqActions(rfqId);

  const detail = rfq.data;
  const failed = intent.isError || bid.isError || withdraw.isError;

  return (
    <QueryState
      pending={rfq.isPending}
      error={rfq.isError}
      empty={detail === null || detail === undefined}
      emptyText="견적·입찰을 찾을 수 없습니다."
    >
      {detail !== null && detail !== undefined && (
        <Detail
          detail={detail}
          failed={failed}
          pending={intent.isPending || bid.isPending || withdraw.isPending}
          onIntent={(value) => intent.mutate(value)}
          onBid={(body) => bid.mutate(body)}
          onWithdraw={() => withdraw.mutate()}
        />
      )}
    </QueryState>
  );
}

type BidInput = { amount: number; vatIncluded: boolean; note: string | null };

function Detail({
  detail,
  failed,
  pending,
  onIntent,
  onBid,
  onWithdraw,
}: {
  detail: VendorRfqDetail;
  failed: boolean;
  pending: boolean;
  onIntent: (value: 'JOIN' | 'DECLINE') => void;
  onBid: (body: BidInput) => void;
  onWithdraw: () => void;
}) {
  const { row, myBid } = detail;
  // 서버가 받는 조건 그대로다(`ExternalRfqService.submit`): 공고중이고 마감 전.
  const canSubmit = row.status === 'OPEN' && !row.closed;
  const live = myBid !== null && myBid.withdrawnAt === null;

  return (
    <>
      <PageHeader
        title={row.title}
        subtitle={`${row.siteName}${row.trade !== null ? ` · ${row.trade}` : ''}`}
        action={
          <div className="flex items-center gap-2">
            <RfqMethodBadge method={row.method} />
            <RfqBadge status={row.status} closed={row.closed} />
          </div>
        }
      />

      {/* 실패는 한 문장이다. 사유를 갈라 보여 주면 그것이 곧 서버의 검사 규칙을 알려 준다. */}
      {failed && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>
          지금은 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      )}

      <dl className="card mb-3 flex flex-col gap-1 text-ui-xs">
        <Line label="마감">
          {shortDateTime(row.closesAt)}
          {!row.closed && row.closesAt !== null && ` · ${untilLabel(row.closesAt)}`}
        </Line>
        {detail.briefingAt !== null && <Line label="현장설명회">{shortDateTime(detail.briefingAt)}</Line>}
        {detail.briefingPlace !== null && <Line label="설명회 장소">{detail.briefingPlace}</Line>}
        {/* 기초금액은 공개로 둔 공고만 내려온다. 없으면 칸을 세우지 않는다 — 「비공개」라고 적는 것도 정보다. */}
        {row.baseAmount !== null && <Line label="기초금액">{won(row.baseAmount)}</Line>}
      </dl>

      {detail.note !== null && (
        <div className="card mb-3">
          <div className="mb-1 text-ui-xs font-medium">공고 내용</div>
          <p className="whitespace-pre-wrap text-ui-sm">{detail.note}</p>
        </div>
      )}

      {detail.items.length > 0 && (
        <div className="card mb-3">
          <div className="mb-2 text-ui-xs font-medium">내역 {detail.items.length}건</div>
          <ul className="flex flex-col gap-2">
            {detail.items.map((item) => (
              <li key={item.id} className="flex items-start justify-between gap-3 text-ui-xs">
                <div className="min-w-0">
                  <div className="truncate">{item.name}</div>
                  {(item.spec !== null || item.note !== null) && (
                    <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                      {[item.spec, item.note].filter((it) => it !== null).join(' · ')}
                    </div>
                  )}
                </div>
                <span className="shrink-0">
                  {item.quantity === null ? '-' : item.quantity.toLocaleString('ko-KR')}
                  {item.unit ?? ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        참가 의사 — 마감 뒤에도 버튼을 끄지 않는다. 못 하는 이유는 눌렀을 때 서버가 답한다.
        고른 쪽이 채워진 버튼으로 남아, 지금 무엇으로 답해 두었는지가 색으로 보인다.
      */}
      <div className="card mb-3">
        <div className="mb-2 text-ui-xs font-medium">참가 의사</div>
        <div className="flex gap-2">
          <button
            type="button"
            className={`${row.intent === 'JOIN' ? 'btn-primary' : 'btn-secondary'} min-h-11 flex-1`}
            onClick={() => onIntent('JOIN')}
            disabled={pending}
          >
            참가
          </button>
          <button
            type="button"
            className={`${row.intent === 'DECLINE' ? 'btn-primary' : 'btn-secondary'} min-h-11 flex-1`}
            onClick={() => onIntent('DECLINE')}
            disabled={pending}
          >
            불참
          </button>
        </div>
      </div>

      <div className="card">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-ui-xs font-medium">{row.method === 'BID' ? '응찰' : '견적 제출'}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            {/* 봉인 중이어도 자기 금액은 보인다 — 자기가 낸 값이라 서버가 풀어 준다(결정 ②). */}
            {live && myBid.sealed && <span className="badge badge-gray">봉인</span>}
            {live && <span className="badge badge-blue">제출 {shortDateTime(myBid.submittedAt)}</span>}
            {myBid !== null && myBid.withdrawnAt !== null && <span className="badge badge-gray">철회함</span>}
          </span>
        </div>

        {/* 낸 값을 되읽어 주는 자리. 폼이 열려 있을 때는 같은 값이 칸 안에 있어 두 번 적지 않는다. */}
        {!canSubmit && live && <MyBid bid={myBid} />}

        {canSubmit ? (
          <>
            {/* 낸 값을 칸에 채워 둔 채로 다시 연다 — 서버가 같은 줄을 고치므로 「고쳐서 다시 내기」가 기본이다. */}
            <BidForm key={`${myBid?.id ?? 'new'}:${myBid?.submittedAt ?? ''}`} bid={live ? myBid : null} pending={pending} onSubmit={onBid} />
            {live && (
              <button type="button" className="btn-secondary mt-2 min-h-11 w-full" onClick={onWithdraw} disabled={pending}>
                철회
              </button>
            )}
          </>
        ) : (
          <p className="text-ui-sm" style={{ color: 'var(--text-muted)' }}>마감되었습니다.</p>
        )}

        {/*
          개찰 결과. `awarded` 가 null 이면 아직 결과가 없는 것이라 아무 말도 하지 않는다.
          거짓이면서 아직 `AWARDED` 가 아닌 것은 「떨어졌다」가 아니라 낙찰자가 정해지지 않은 상태다.
        */}
        {detail.awarded !== null && (
          <p
            className="mt-3 text-ui-sm font-medium"
            style={{ color: detail.awarded ? 'var(--accent)' : 'var(--text-muted)' }}
          >
            {detail.awarded
              ? '낙찰되었습니다.'
              : row.status === 'AWARDED'
                ? '낙찰되지 않았습니다.'
                : '개찰되었습니다.'}
          </p>
        )}
      </div>
    </>
  );
}

/** 우리가 낸 값. 금액·부가세·비고까지다 — 남의 것은 한 줄도 오지 않는다. */
function MyBid({ bid }: { bid: RfqBidView }) {
  return (
    <dl className="mb-3 flex flex-col gap-1 text-ui-xs">
      <Line label="금액">
        <span className="text-ui-md font-semibold">{won(bid.amount)}</span>
        {bid.vatIncluded !== null && (
          <span className="ml-1" style={{ color: 'var(--text-muted)' }}>
            {bid.vatIncluded ? '(부가세 포함)' : '(부가세 별도)'}
          </span>
        )}
      </Line>
      {bid.note !== null && <Line label="비고">{bid.note}</Line>}
    </dl>
  );
}

/**
 * 응찰 폼.
 *
 * <p>이미 낸 값에서 시작한다 — 초깃값을 `useState` 로 한 번만 읽고, 다시 채우는 일은 부모가
 * `key` 를 갈아 맡긴다. 효과로 칸을 덮으면 적는 중에 서버 응답이 도착해 손으로 친 숫자가 사라진다.
 */
function BidForm({
  bid,
  pending,
  onSubmit,
}: {
  bid: RfqBidView | null;
  pending: boolean;
  onSubmit: (body: BidInput) => void;
}) {
  const [amount, setAmount] = useState(() => (bid === null || bid.amount === null ? '' : String(bid.amount)));
  const [vatIncluded, setVatIncluded] = useState(() => bid?.vatIncluded ?? false);
  const [note, setNote] = useState(() => bid?.note ?? '');

  const digits = amount.replace(/[^0-9]/g, '');

  function submit(event: FormEvent) {
    event.preventDefault();
    onSubmit({
      amount: Number(digits),
      vatIncluded,
      note: note.trim() === '' ? null : note.trim(),
    });
  }

  return (
    <form onSubmit={submit}>
      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>금액</span>
        <input
          type="text"
          inputMode="numeric"
          className="input-field"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="0"
          required
        />
        {/* 적은 숫자를 자릿점 찍어 되읽어 준다 — 0 을 하나 더 친 것이 가장 흔한 사고다. */}
        {digits !== '' && (
          <span className="mt-1 block text-ui-2xs" style={{ color: 'var(--text-muted)' }}>{won(Number(digits))}</span>
        )}
      </label>

      <label className="mb-3 flex min-h-11 items-center gap-2 text-ui-sm">
        <input type="checkbox" checked={vatIncluded} onChange={(event) => setVatIncluded(event.target.checked)} />
        부가세 포함
      </label>

      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>비고</span>
        <input type="text" className="input-field" value={note} onChange={(event) => setNote(event.target.value)} />
      </label>

      <button type="submit" className="btn-primary min-h-11 w-full" disabled={pending || digits === ''}>
        {pending ? '보내는 중입니다…' : bid === null ? '제출' : '다시 제출'}
      </button>
    </form>
  );
}

function Line({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt style={{ color: 'var(--text-secondary)' }}>{label}</dt>
      <dd className="text-right">{children}</dd>
    </div>
  );
}
